import { useState, useEffect, useContext } from 'react';
import { Box, CircularProgress, Link, Typography } from '@mui/material';
import { format, parseISO, isBefore } from 'date-fns';
import { useTranslation } from 'react-i18next';
import {
    AppContext,
    SttTabs,
    SttFeeList,
    SttDialog,
    SttLabelPeriod,
    SttList,
} from '../../all';
import {
    createObjectWithUpdatedFieldsOnly,
    getErrorMessage,
    subsetObject,
} from '../../../lib/utils';
import SttGroupMemberDialogDetail from './SttGroupMemberDialogDetail';
import SttBeneficiariesList from '../../lists/SttBeneficiariesList';
import DataphonePaymentDialog from '../../../components/dialogs/DataphonePaymentDialog';
import POSStripePaymentDialog from '../../../components/stripe/POSStripePaymentDialog';
import SttError from '../../../components/error/SttError';
import useGroupsService from '../../../services/GroupsService';
import constants from '../../../config/constants';
import { useLoader } from '../../../lib/hooks';

type Fee = {
    id: number;
    [key: string]: unknown;
};

type Discount = {
    id: number;
    name: string;
    discountPercentage: number;
    checked?: boolean;
};

type Enrollment = {
    price?: number;
    paymentForm?: string;
    priceShape?: {
        currency?: string;
        locale?: string;
    };
};

type User = {
    id?: number;
    mship: {
        fullName: string;
        user?: {
            id: number;
        };
    };
};

type Group = {
    id: number;
    type?: string;
    ini?: string;
    end?: string;
    enrollmentPrice?: number;
    feePrice?: number;
    expiration?: string | null;
    fee?: number;
    feeShape?: {
        currency?: string;
        locale?: string;
    };
};

type GroupMember = {
    id?: number;
    name: string;
    notes?: string;
    ini?: string;
    end?: string;
    paymentFormFee?: string;
    user: User;
    enrollment: Enrollment;
    group: {
        id: number;
        fee?: number;
        feeShape?: {
            currency?: string;
            locale?: string;
        };
    };
    expirationCheck?: boolean;
    fees?: Fee[];
    discounts?: Discount[];
};

type HistoryItem = {
    id: number;
    ini?: string;
    end?: string;
};

type HistoryResponse = {
    rows: HistoryItem[];
};

type TPVData = {
    id: number;
    price: number;
    concept: string;
    payment?: {
        paymentForm: string;
    };
    sc?: {
        id: number;
        short: string;
        customer?: {
            name: string;
            cif: string;
        };
        address?: string;
        phonePublic?: string;
    };
};

type POSPaymentParams = {
    amount: number;
    concept: string;
    idTpv: number;
    sc: {
        id: number;
        scName: string;
        entityName: string;
        address?: string;
        cif: string;
        phone?: string;
    };
    returnParams: {
        groupMember: GroupMember;
    };
};

type Props = {
    idGroupMember?: number | string | null;
    group: Group;
    onClose?: () => void;
    onUpdateMember?: (member: GroupMember) => void;
    trash?: boolean;
};

/**
 *    Group member of a group
 *    =======================
 *
 * Props
 *
 *    * integer idGroupMember
 *     * SttGroup group - the parent group (as we need some data for conditional operations)
 *     * function onClose : callback when 'close' button or ESC key is pressed
 *     * function onUpdateMember: after adding, updating and deleting.
 */
export default function SttGroupMemberDialog({
    idGroupMember = null,
    group,
    onClose,
    onUpdateMember,
    trash = false,
}: Props): React.ReactElement | null {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const [member, setMember] = useState<GroupMember | undefined>();
    const [discountsForm, setDiscountsForm] = useState<Discount[]>([]);
    const [creating, setCreating] = useState(false);
    const [history, setHistory] = useState<HistoryResponse>();
    const [posPaymentParams, setPosPaymentParams] =
        useState<POSPaymentParams | null>(null);
    const [currentMemberState, setCurrentMemberState] = useState<
        GroupMember | undefined
    >();
    const groupsService = useGroupsService();
    const getGroupDiscountsQuery = groupsService.useGetGroupDiscounts(
        group.id,
        {},
        {
            refetchOnMount: false,
            enabled: creating,
        },
    );

    // Handle discount data when query succeeds
    useEffect(() => {
        if (getGroupDiscountsQuery.data?.rows) {
            setDiscountsForm(
                getGroupDiscountsQuery.data.rows.map((discount: any) => ({
                    ...discount,
                    checked: false,
                })),
            );
        }
    }, [getGroupDiscountsQuery.data]);

    const createGroupMemberMutation = groupsService.useCreateGroupMember({
        onSuccess: ((response: { tpv: TPVData; groupMember: GroupMember }) => {
            const { tpv, groupMember } = response;

            if (
                tpv?.payment?.paymentForm ===
                constants.payment.paymentForms.dataphoneConnected.name
            ) {
                setPosPaymentParams({
                    amount: tpv.price ?? 0,
                    concept: tpv.concept ?? '',
                    idTpv: tpv.id ?? 0,
                    sc: {
                        id: tpv?.sc?.id!,
                        scName: tpv?.sc?.short!,
                        entityName: tpv?.sc?.customer?.name!,
                        address: tpv?.sc?.address,
                        cif: tpv?.sc?.customer?.cif!,
                        phone: tpv?.sc?.phonePublic,
                    },
                    returnParams: {
                        groupMember,
                    },
                });
            } else {
                onUpdateMember?.(response.groupMember);
                setCurrentMemberState(response?.groupMember);
            }
        }) as any,
        onError: (error: unknown) => {
            cxt?.showMessage('E', getErrorMessage(error));
        },
    });
    const updateGroupMemberMutation = groupsService.useUpdateGroupMember({
        onSuccess: ((response: { groupMember: GroupMember }) => {
            onUpdateMember?.(response.groupMember);
            setCurrentMemberState(response?.groupMember);
        }) as any,
        onError: (error: unknown) => {
            cxt?.showMessage('E', getErrorMessage(error));
            setMember(currentMemberState);
        },
    });

    const loadHistory = () => {
        if (!member?.user?.id) return;

        cxt?.api(
            'GET',
            `/groups/${member.group.id}/users/${member.user.id}/members`,
            {
                success: (r: HistoryResponse) => {
                    setHistory(r);
                },
            },
        );
    };

    const loadGroupMember = () => {
        cxt?.api('GET', `/groups/members/${idGroupMember}`, {
            params: {
                trash,
            },
            success: (r: { groupMember: GroupMember }) => {
                setMember(r.groupMember);
                setCurrentMemberState(r.groupMember);
            },
        });
    };

    useEffect(() => {
        if (member?.id) {
            loadHistory();
        }
    }, [member?.id]);

    /**
     * Group member id changes
     */
    useEffect(() => {
        // Load group member
        if (idGroupMember === 'create') {
            // Group member data by default
            const now = new Date();
            setMember({
                name: '',
                notes: '',
                ini: isBefore(parseISO(group.ini!), now)
                    ? format(now, 'yyyy-MM-dd')
                    : format(parseISO(group.ini!), 'yyyy-MM-dd'),
                end: format(parseISO(group.end!), 'yyyy-MM-dd'),
                paymentFormFee: '',
                user: {
                    mship: {
                        fullName: '',
                    },
                },
                enrollment: {
                    price: group.enrollmentPrice,
                    paymentForm: '',
                },
                group: {
                    id: group.id,
                    fee: group.feePrice,
                },
                expirationCheck: !(
                    group.expiration === constants.bono.bonoExpirationDefault ||
                    group.expiration === null
                ), // Check para comprobar si se envia parametro end o no
            });
            setCreating(true);
        } else {
            setCreating(false);
            loadGroupMember();
        }
    }, [idGroupMember]);

    function handleChangeDiscountsForm({
        name: id,
        value,
    }: {
        name: string | number | undefined;
        value: unknown;
    }) {
        if (!id) return;
        setDiscountsForm((prev: any) =>
            prev.map((discount: any) =>
                discount.id === id
                    ? {
                          ...discount,
                          checked: value as boolean,
                      }
                    : discount,
            ),
        );
    }

    function handleChange({ name, value }: { name: string; value: unknown }) {
        if (!name) return;
        let updatedName = name;
        let updatedValue = value;

        if (name === 'paymentFormSignup') {
            updatedName = 'enrollment';
            updatedValue = {
                ...member?.enrollment,
                paymentForm: value,
            };
        }

        if (name === 'mship') {
            updatedName = 'user';
            updatedValue = {
                mship: value,
            };
        }

        setMember(
            (prev: any) =>
                ({ ...prev, [updatedName]: updatedValue }) as GroupMember,
        );
    }

    function upsertMember() {
        if (!member) return;

        let paramsFilter = ['ini', 'end', 'notes', 'paymentFormFee']; // No se debe incluir expirationCheck

        if (member.expirationCheck) {
            paramsFilter = paramsFilter.filter(
                (parameter) => parameter !== 'end',
            );
        }
        const params = createObjectWithUpdatedFieldsOnly(
            subsetObject(member, paramsFilter.join(',')),
            currentMemberState,
        );

        const discountsIdsToSend = discountsForm
            .filter((discount: any) => discount.checked)
            .map((discount: any) => discount.id);

        if (creating) {
            // Get user ID from mship - the user.id field should be populated by the API
            const mshipData = member.user?.mship as any;
            const idUser = mshipData?.user?.id;

            if (!idUser) {
                cxt?.showMessage('E', t('sc.groups.members.selectUserError'));
                return;
            }

            const apiParams: any = {
                ...params,
                idUser,
                paymentFormSignup: member.enrollment?.paymentForm,
                discountsIds: discountsIdsToSend,
            };

            createGroupMemberMutation.mutate({
                id: group.id,
                params: apiParams,
            });
        } else {
            updateGroupMemberMutation.mutate({
                id: member.id!,
                params,
            });
        }
    }

    function deleteMember() {
        if (!member) return;

        cxt?.api('PUT', `/groups/members/${member.id}/unsubscribe`, {
            confirmation: true,
            success: (r: { groupMember: GroupMember }) => {
                onUpdateMember?.(r.groupMember);
            },
        });
    }

    function deleteFutureMemberPeriod(id: number) {
        cxt?.api('DELETE', `/groups/members/${id}`, {
            confirmation: true,
            success: (r: { groupMember: GroupMember }) => {
                onUpdateMember?.(r.groupMember);
            },
        });
    }

    const [isMutationsLoading, mutationsLoader] = useLoader([
        createGroupMemberMutation.status,
        updateGroupMemberMutation.status,
    ]);

    if (isMutationsLoading) {
        return mutationsLoader;
    }

    let dialogContent: React.ReactNode;
    if (getGroupDiscountsQuery.isLoading) {
        dialogContent = (
            <Box display="flex" justifyContent="center" paddingY={12}>
                <CircularProgress />
            </Box>
        );
    } else if (getGroupDiscountsQuery.isError) {
        dialogContent = <SttError />;
    } else if (!member) {
        return null;
    } else {
        dialogContent = (
            <SttTabs
                tabs={[
                    {
                        caption: t('sc.groups.members.details'),
                        component: (
                            <SttGroupMemberDialogDetail
                                member={member}
                                creating={creating}
                                handleChange={handleChange}
                                group={group}
                                discounts={
                                    creating ? discountsForm : member.discounts
                                }
                                handleChangeDiscounts={
                                    handleChangeDiscountsForm
                                }
                            />
                        ),
                    },
                    {
                        show: !creating,
                        caption: t('generic.history'),
                        component: (
                            <SttList
                                numRows={5}
                                data={history?.rows.map((ug: any) => ({
                                    caption: (
                                        <>
                                            {ug.ini && ug.end ? (
                                                <SttLabelPeriod
                                                    ini={ug.ini}
                                                    end={ug.end}
                                                    dateWithTime
                                                />
                                            ) : (
                                                <Typography
                                                    variant="subtitle1"
                                                    display="inline"
                                                >
                                                    {t('PeriodWithoutDate')}
                                                </Typography>
                                            )}
                                            <Link
                                                onClick={() => {
                                                    deleteFutureMemberPeriod(
                                                        ug.id,
                                                    );
                                                }}
                                            >
                                                &nbsp;&nbsp;
                                                {t('Delete')}
                                            </Link>
                                        </>
                                    ),
                                }))}
                            />
                        ),
                    },
                    {
                        show: group.feePrice !== undefined && !creating,
                        caption: t('Fees'),
                        component: (
                            <SttFeeList fees={(member.fees as any) ?? []} />
                        ),
                    },
                    {
                        show: group.type === 'FAMILY' && !creating,
                        caption: t('Beneficiaries'),
                        component: (
                            <SttBeneficiariesList member={member as any} />
                        ),
                    },
                ]}
            />
        );
    }

    return (
        <>
            <SttDialog
                open={idGroupMember !== null}
                title={t('sc.groups.members.details')}
                onClose={() => (onClose ? onClose() : null)}
                maxWidth="md"
                content={dialogContent}
                buttons={[
                    {
                        show:
                            !getGroupDiscountsQuery.isLoading &&
                            !getGroupDiscountsQuery.isError,
                        type: 'save',
                        onClick: upsertMember,
                    },
                    {
                        show:
                            !creating &&
                            !getGroupDiscountsQuery.isLoading &&
                            !getGroupDiscountsQuery.isError,
                        type: 'unsubscribe',
                        onClick: deleteMember,
                    },
                ]}
            />

            {cxt?.sc?.sporttiaStripeLocationId && posPaymentParams !== null ? (
                <POSStripePaymentDialog
                    paymentData={posPaymentParams}
                    onClose={() => setPosPaymentParams(null)}
                    onSuccess={() => {
                        onUpdateMember?.(
                            posPaymentParams.returnParams.groupMember,
                        );
                    }}
                />
            ) : (
                <DataphonePaymentDialog
                    open={posPaymentParams !== null}
                    paymentData={posPaymentParams}
                    onClose={() => setPosPaymentParams(null)}
                    onSuccess={
                        ((
                            _ticket: unknown,
                            returnParams: { groupMember: GroupMember },
                        ) => {
                            onUpdateMember?.(returnParams.groupMember);
                        }) as any
                    }
                />
            )}
        </>
    );
}
